// Line-count audit (2026-05-04): 533 lines.
import { useCallback } from 'react';
import { chatApi, userAgentApi, type ChatResponse, type EditorContext } from '@/services/api/agentApi';
import { getAgentSocket } from '@/services/api/agentSocketClient';
import { classifyRoute } from '@/services/editor/AgentChatController';
import { EditorContextSerializer } from '@/services/editor/EditorContextSerializer';
import * as agentConversationManager from '@/services/editor/AgentConversationManager';
import * as canvasSessionManager from '@/services/editor/CanvasSessionManager';
import { useDebugStore } from '@/stores/ai/debug-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import {
  buildAgentContext,
  getAgentThreadKey,
  getUserAgentThreadKey,
  shouldPreferUserAgentContinuation,
} from '@/utils/agentContextBuilder';
import { shouldIncludeBreakpointStylesForRoute } from './styleContextEnrichment';
import type { PropertyMessage } from '@/components/ai/types';
import type { UseAgentRoutingDeps, UseAgentRoutingReturn } from './agentMessagingTypes';

export function useAgentRouting(deps: UseAgentRoutingDeps): UseAgentRoutingReturn {
  const {
    component,
    isCanvas,
    messages,
    setMessages,
    setInputValue,
    setIsLoading,
    agentThreadIdRef,
    agentVisualThreadIdRef,
    userAgentThreadIdRef,
    streamingActionCountRef,
    pendingConfirmationActionsRef,
    setStreamingSteps,
    setStreamingMessage,
    setIsStreamingActive,
    setMultiPageProgress,
    project,
    storePages,
    agentStatus,
    userCommunity,
    tradeScope,
    persistMessage,
    generateMessageId,
    buildStreamingCallbacks,
  } = deps;

  const mapAgentResponseToMessage = useCallback((response: ChatResponse): PropertyMessage => ({
    id: generateMessageId(),
    content: response.response,
    role: 'assistant',
    timestamp: new Date(),
    metadata: {
      source: 'agent',
      threadId: response.threadId,
      modelName: response.usage?.modelName,
      modelTier: response.usage?.modelTier,
      memoriesCreated: response.memoriesCreated,
      eventsCreated: response.eventsCreated,
      agentData: response.metadata,
      memoriesUsed: response.metadata?.memoriesUsed,
    },
  }), [generateMessageId]);

  const persistMessageSafely = useCallback((message: PropertyMessage): void => {
    void persistMessage(message).catch(() => undefined);
  }, [persistMessage]);

  const storeThreadIdSafely = useCallback((key: string, threadId: string): void => {
    try {
      localStorage.setItem(key, threadId);
    } catch {
      return;
    }
  }, []);

  const shouldAutoPopulateHomeCanvasAfterAgentCreation = useCallback((responseContent: string): boolean => {
    const state = useEditorStore.getState();
    const currentPage = (storePages ?? []).find((page) => page.id === state.currentPageId);
    const recentConversation = [...messages.slice(-12).map((message) => message.content), responseContent].join('\n');

    return /\b(created|built)\b[\s\S]*\bweight tracker dashboard\b[\s\S]*\bagent\b/i.test(responseContent)
      && /home/i.test(currentPage?.name || '')
      && state.components.length === 0
      && /\b(populate|fill|build|generate)\b[\s\S]*\b(home|canvas|dashboard overview)\b|\bempty\s+home\b|\bhome\s+canvas\b/i.test(recentConversation);
  }, [messages, storePages]);

  const populateHomeCanvasAfterAgentCreation = useCallback(async (): Promise<void> => {
    const task = [
      'Populate the empty Home page with a unified Weight Tracker dashboard overview.',
      'Use the existing Weight Tracker page and project data as the source of truth.',
      'Create a polished dashboard with KPI cards for latest weight, goal weight, progress to goal, pounds lost, and projected days.',
      'Add a progress bar toward the 170 lb goal, a weight trend chart, a projection/on-track section, and a compact recent entries summary.',
      'Use the existing Health 2 visual style and bind components to the existing Weight Tracker data where available.',
      'Do not create a new page; populate the current Home page.',
    ].join(' ');
    const noticeMessage: PropertyMessage = {
      id: generateMessageId(),
      content: 'The agent is created. Now I’ll populate the empty Home page with the dashboard overview.',
      role: 'assistant',
      timestamp: new Date(),
      metadata: { source: 'user-agent' },
    };
    setMessages((previous) => [...previous, noticeMessage]);
    persistMessageSafely(noticeMessage);

    const loadingMessage: PropertyMessage = {
      id: generateMessageId(),
      content: 'Populating the Home page...',
      role: 'assistant',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages((previous) => [...previous, loadingMessage]);

    try {
      const editorContext: EditorContext = EditorContextSerializer.serialize();
      const threadId = agentVisualThreadIdRef.current || undefined;
      const sessionContext = threadId
        ? canvasSessionManager.getSessionContext(threadId)
        : undefined;

      streamingActionCountRef.current = 0;
      pendingConfirmationActionsRef.current = [];

      await agentConversationManager.routeToVisualEditorStreaming(
        task,
        editorContext,
        buildStreamingCallbacks(loadingMessage, task),
        threadId,
        sessionContext,
        {
          isFollowUp: false,
          complexity: 'data-page-generation',
          mentionedBreakpoints: [],
        },
      );
    } catch (error: unknown) {
      const errorMessage: PropertyMessage = {
        id: generateMessageId(),
        content: `The agent was created, but I couldn't populate the Home page automatically: ${error instanceof Error ? error.message : String(error)}`,
        role: 'assistant',
        timestamp: new Date(),
        metadata: { source: 'user-agent', error: true },
      };
      setMessages((previous) => [...previous.filter((message) => message.id !== loadingMessage.id), errorMessage]);
      persistMessageSafely(errorMessage);
    }
  }, [
    agentVisualThreadIdRef,
    buildStreamingCallbacks,
    generateMessageId,
    pendingConfirmationActionsRef,
    persistMessageSafely,
    setMessages,
    streamingActionCountRef,
  ]);

  const routeToUserAgent = useCallback(
    async (userInput: string): Promise<void> => {
      const userMessage: PropertyMessage = {
        id: generateMessageId(),
        content: userInput,
        role: 'user',
        timestamp: new Date(),
      };
      setMessages((previous) => [...previous, userMessage]);
      persistMessageSafely(userMessage);
      setInputValue('');
      setIsLoading(true);

      const loadingMessage: PropertyMessage = {
        id: generateMessageId(),
        content: 'Thinking...',
        role: 'assistant',
        timestamp: new Date(),
        isStreaming: true,
        metadata: { source: 'user-agent' },
      };
      setMessages((previous) => [...previous, loadingMessage]);

      try {
        const editorStoreState = useEditorStore.getState();
        const editorContext = buildAgentContext(
          storePages,
          isCanvas ?? false,
          component,
          project,
          {
            currentPageId: editorStoreState.currentPageId,
            currentPageComponents: editorStoreState.components,
            pageComponents: editorStoreState.pageComponents,
          }
        );
        const enrichedUserInput = editorContext
          ? `[Editor Context: ${editorContext}]\n\n${userInput}`
          : userInput;
        const chatHistory = messages.slice(-10).map((message) => ({
          role: message.role,
          content: message.content,
        }));
        const result = await userAgentApi.chat(
          enrichedUserInput,
          chatHistory,
          userAgentThreadIdRef.current || undefined
        );

        if (result?.data?.threadId) {
          userAgentThreadIdRef.current = result.data.threadId;
          if (project?.id) {
            storeThreadIdSafely(getUserAgentThreadKey(project.id), result.data.threadId);
          }
        }

        if (result?.data?.pendingTaskId) {
          const acknowledgementMessage: PropertyMessage = {
            id: generateMessageId(),
            content: result?.data?.response || 'Looking into that for you...',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
            metadata: { source: 'user-agent', pendingTaskId: result.data.pendingTaskId },
          };
          setMessages((previous) => [
            ...previous.filter((message) => message.id !== loadingMessage.id),
            acknowledgementMessage,
          ]);
          setIsLoading(false);

          const taskId = result.data.pendingTaskId;
          let resolved = false;
          let pollTimer: ReturnType<typeof setInterval> | null = null;
          let cleanupTimer: ReturnType<typeof setTimeout> | null = null;
          let socket: ReturnType<typeof getAgentSocket> | null = null;

          const deliverResult = (content: string) => {
            if (resolved) {
              return;
            }
            resolved = true;
            if (pollTimer) {
              clearInterval(pollTimer);
            }
            if (cleanupTimer) {
              clearTimeout(cleanupTimer);
            }
            if (socket) {
              socket.off('user_agent:task_result', onSocketResult);
              socket.off('user_agent:task_progress', onSocketProgress);
            }

            const taskResultMessage: PropertyMessage = {
              id: generateMessageId(),
              content,
              role: 'assistant',
              timestamp: new Date(),
              metadata: { source: 'user-agent' },
            };
            setMessages((previous) => [
              ...previous.filter((message) => message.id !== acknowledgementMessage.id),
              taskResultMessage,
            ]);
            persistMessageSafely(taskResultMessage);
          };

          const formatContent = (data: { response?: string; error?: string; warnings?: string[] }) =>
            data.error
              ? `I wasn't able to get that information: ${data.error}`
              : `${data.warnings?.length ? `${data.warnings.map((warning) => `⚠️ ${warning}`).join('\n')}\n\n` : ''}${data.response || ''}`;

          const onSocketResult = (data: { taskId: string; response?: string; error?: string; warnings?: string[] }) => {
            if (data.taskId !== taskId) {
              return;
            }
            deliverResult(formatContent(data));
          };

          const onSocketProgress = (data: { taskId: string; summary: string }) => {
            if (data.taskId !== taskId || resolved) {
              return;
            }
            setMessages((previous) => previous.map((message) =>
              message.id === acknowledgementMessage.id
                ? { ...message, content: data.summary }
                : message
            ));
          };

          try {
            socket = getAgentSocket({ connect: true });
            socket.on('user_agent:task_result', onSocketResult);
            socket.on('user_agent:task_progress', onSocketProgress);
          } catch {
            socket = null;
          }

          setTimeout(() => {
            if (resolved) {
              return;
            }

            pollTimer = setInterval(async () => {
              if (resolved) {
                if (pollTimer) {
                  clearInterval(pollTimer);
                }
                return;
              }

              try {
                const pollResult = await userAgentApi.pollTaskResult(taskId);
                if (pollResult?.data?.status === 'completed') {
                  deliverResult(
                    pollResult.data.error
                      ? `I wasn't able to get that information: ${pollResult.data.error}`
                      : pollResult.data.response || 'Research completed.'
                  );
                } else if (pollResult?.data?.status === 'not_found' && pollTimer) {
                  clearInterval(pollTimer);
                }
              } catch {
                return;
              }
            }, 5000);
          }, 5000);

          cleanupTimer = setTimeout(() => {
            if (!resolved) {
              deliverResult('The research is taking longer than expected. Please try again.');
            }
          }, 180000);

          return;
        }

        const agentResponse: PropertyMessage = {
          id: generateMessageId(),
          content: result?.data?.response || 'I received your message.',
          role: 'assistant',
          timestamp: new Date(),
          metadata: { source: 'user-agent' },
        };
        setMessages((previous) => [
          ...previous.filter((message) => message.id !== loadingMessage.id),
          agentResponse,
        ]);
        persistMessageSafely(agentResponse);

        if (shouldAutoPopulateHomeCanvasAfterAgentCreation(agentResponse.content)) {
          await populateHomeCanvasAfterAgentCreation();
        }
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        const errorMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `Sorry, I couldn't process that request: ${message || 'Unknown error'}`,
          role: 'assistant',
          timestamp: new Date(),
          metadata: { source: 'user-agent', error: true },
        };
        setMessages((previous) => [
          ...previous.filter((current) => current.id !== loadingMessage.id),
          errorMessage,
        ]);
      }

      setIsLoading(false);
    },
    [
      component,
      isCanvas,
      messages,
      setMessages,
      setInputValue,
      setIsLoading,
      userAgentThreadIdRef,
      project,
      storePages,
      storeThreadIdSafely,
      persistMessageSafely,
      generateMessageId,
      shouldAutoPopulateHomeCanvasAfterAgentCreation,
      populateHomeCanvasAfterAgentCreation,
    ]
  );

  const routeToAgent = useCallback(
    async (
      userInput: string,
      contextPrefix?: string,
      loadingId?: string
    ): Promise<void> => {
      const communityId = userCommunity?.community_id;
      const enrichedMessage = contextPrefix
        ? `[Editor Context: ${contextPrefix}]\n\n${userInput}`
        : userInput;

      const agentResponse = await chatApi.send(
        enrichedMessage,
        communityId,
        agentThreadIdRef.current || undefined
      );

      if (!agentResponse.data) {
        throw new Error('Agent service returned empty response');
      }

      if (agentResponse.data.threadId) {
        agentThreadIdRef.current = agentResponse.data.threadId;
        if (project?.id) {
          storeThreadIdSafely(getAgentThreadKey(project.id), agentResponse.data.threadId);
        }
      }

      const agentMessage = mapAgentResponseToMessage(agentResponse.data);
      setMessages((previous) => [
        ...previous.filter((message) => message.id !== loadingId),
        agentMessage,
      ]);
      persistMessageSafely(agentMessage);
    },
    [
      userCommunity,
      agentThreadIdRef,
      project,
      storeThreadIdSafely,
      mapAgentResponseToMessage,
      setMessages,
      persistMessageSafely,
    ]
  );

  const handleRoutedMessage = useCallback(
    async (userInput: string): Promise<{ handled: boolean }> => {
      const preferUserAgentContinuation = shouldPreferUserAgentContinuation(
        userInput,
        messages,
        userAgentThreadIdRef
      );
      if (preferUserAgentContinuation) {
        await routeToUserAgent(userInput);
        return { handled: true };
      }

      const routeClassification = await classifyRoute(userInput, {
        hasSelectedComponent: Boolean(component),
        hasSelectedComponentType: component?.type,
        agentAvailable: agentStatus.available,
        hasSessionContext: (() => {
          const currentThreadId = agentVisualThreadIdRef.current;
          return currentThreadId
            ? canvasSessionManager.hasSession(currentThreadId)
            : false;
        })(),
        preferUserAgentContinuation,
        tradeScope,
      });

      if (routeClassification.route === 'visual-editor-stream') {
        const userMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput,
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => [...previous, userMessage]);
        persistMessageSafely(userMessage);
        setInputValue('');

        const loadingMessage: PropertyMessage = {
          id: generateMessageId(),
          content: 'Building your layout...',
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: true,
        };
        setMessages((previous) => [...previous, loadingMessage]);

        try {
          const editorContext: EditorContext = EditorContextSerializer.serialize({
            includeBreakpointStyles: shouldIncludeBreakpointStylesForRoute(routeClassification),
          });
          const threadId = agentVisualThreadIdRef.current || undefined;
          const sessionContext = threadId
            ? canvasSessionManager.getSessionContext(threadId)
            : undefined;

          if (useDebugStore.getState().isEnabled) {
            const debugStore = useDebugStore.getState();
            debugStore.startSession(userInput, routeClassification.route);
            debugStore.setEditorContext(editorContext);
            if (sessionContext) {
              debugStore.setSessionContext(sessionContext);
            }
            debugStore.setRoutePatternMatched(userInput, {
              hasSelectedComponent: Boolean(component),
              agentAvailable: agentStatus.available,
              hasSessionContext: threadId
                ? canvasSessionManager.hasSession(threadId)
                : false,
            });
          }

          streamingActionCountRef.current = 0;
          pendingConfirmationActionsRef.current = [];
          await agentConversationManager.routeToVisualEditorStreaming(
            userInput,
            editorContext,
            buildStreamingCallbacks(loadingMessage, userInput),
            threadId,
            sessionContext,
            routeClassification.metadata,
          );

          setIsLoading(false);
          return { handled: true };
        } catch {
          setIsStreamingActive(false);
          setStreamingSteps([]);
          setStreamingMessage(null);
          setMultiPageProgress(null);
          setMessages((previous) => previous.filter((message) => message.id !== loadingMessage.id));
          setIsLoading(false);
        }
      }

      if (routeClassification.route === 'user-agent') {
        await routeToUserAgent(userInput);
        return { handled: true };
      }

      return { handled: false };
    },
    [
      messages,
      userAgentThreadIdRef,
      routeToUserAgent,
      component,
      agentStatus,
      agentVisualThreadIdRef,
      tradeScope,
      generateMessageId,
      setMessages,
      persistMessageSafely,
      setInputValue,
      streamingActionCountRef,
      pendingConfirmationActionsRef,
      buildStreamingCallbacks,
      setIsLoading,
      setIsStreamingActive,
      setStreamingSteps,
      setStreamingMessage,
      setMultiPageProgress,
    ]
  );

  return {
    handleRoutedMessage,
    routeToUserAgent,
    routeToAgent,
  };
}
